import React from "react";
import { Helmet } from "react-helmet";
import Header from "../homepage/components/Header";
import Footer from "../homepage/components/Footer";
import Seo from "../../components/Seo";

const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Skyline VFX Services",
  serviceType: [
    "Digital matte painting",
    "Environment design",
    "Concept art",
    "Visual development & Art Direction",
    "Set extension",
    "2.5D projection / camera mapping",
    "VFX software and pipeline development",
  ],
  provider: {
    "@type": "Organization",
    name: "Skyline VFX",
    url: "https://skylinevfx.com",
  },
};

const ServicesPage = () => {
  return (
    <>
      <Seo
        title="Services - Skyline VFX"
        description="Digital matte painting, environment design, concept art, visual development, and VFX software and pipeline development for film and television."
        schema={servicesSchema}
      />
      {/* Clash Display: only for the Neevii Ink wordmark, matching the neevii.com/ink header. */}
      <Helmet>
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="" />
        <link href="https://api.fontshare.com/v2/css?f[]=clash-display@600&display=swap" rel="stylesheet" />
      </Helmet>
      <div className="min-h-screen text-white">
        <Header />
        <main className="pt-32 pb-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-3 text-center">
              <h1 className="text-4xl lg:text-5xl font-bold">Services</h1>
              <p className="text-gray-300 text-lg">
                Digital matte painting, environment design, concept art, visual development, and VFX software and pipeline development for film and television.
              </p>
            </header>

            <section className="border border-white/10 bg-white/5 p-6 space-y-3">
              <h2 className="text-2xl font-semibold">Core services</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-300">
                <li>Visual development &amp; Art Direction</li>
                <li>Digital matte painting (photoreal environments)</li>
                <li>Environment design and concept art</li>
                <li>Set extensions for VFX</li>
                <li>2.5D projection / camera mapping</li>
                <li>Book cover illustration</li>
                <li>VFX software and pipeline development</li>
              </ul>
            </section>

            <section className="border border-white/10 bg-white/5 p-6 space-y-3">
              <h2 className="text-2xl font-semibold">VFX Software &amp; Pipeline Development</h2>
              <p className="text-gray-300">
                Artist-led software and tools built around the needs of VFX production, alongside digital matte painting and visual development.
              </p>
              <a
                href="https://www.neevii.com/ink"
                aria-label="Neevii Ink"
                className="neevii-ink-brand inline-flex items-center gap-[9px] text-[26px] font-semibold tracking-[-0.02em] text-[#F5F1FA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ffab3e]"
                style={{ fontFamily: "'Clash Display', Inter, sans-serif" }}
              >
                <img src="/assets/neevii/neevii-ink-logo.webp" alt="" width="48" height="48" className="block w-12 h-12" />
                <span>
                  Neevii
                  <span
                    className="ml-[0.14em] bg-clip-text text-transparent"
                    style={{ backgroundImage: "linear-gradient(100deg, #8B5CF6 0%, #E879F9 45%, #FE9352 100%)" }}
                  >
                    Ink
                  </span>
                </span>
              </a>
              <p className="text-gray-300">
                Neevii Ink is an image editor built for VFX pipelines, with scene-linear workflows, OCIO colour management and local AI tools.
              </p>
              <a href="https://www.neevii.com/ink" className="block border border-white/10 hover:border-[#ffab3e]/60 transition-colors">
                <img
                  src="/assets/neevii/neevii-ink-ui.webp"
                  alt="Neevii Ink workspace: a fantasy landscape being graded with Color Correct controls, alongside brush settings and a Layers panel"
                  width="1600"
                  height="934"
                  loading="lazy"
                  className="block w-full h-auto"
                />
              </a>
              <a
                href="https://www.neevii.com/ink"
                className="inline-block text-[#ffab3e] hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ffab3e]"
              >
                Explore Neevii Ink &amp; VFX Tools
              </a>
            </section>

            <section className="border border-white/10 bg-white/5 p-6 space-y-3">
              <h2 className="text-2xl font-semibold">Typical deliverables</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-300">
                <li>Layered matte paintings and projection-ready assets</li>
                <li>Environment concept art and design exploration</li>
                <li>Final comp plates aligned with production requirements</li>
              </ul>
            </section>

          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default ServicesPage;
